import type { ScheduleSummaryTotals } from '../types/schedule';
import type { ScheduleStatusFilter } from './scheduleFilters';

/**
 * A single non-routine condition surfaced by the AnomalyBanner. Each
 * anomaly carries the strategy column-filter value the chip should
 * toggle on click (when the host view supports it); informational-only
 * anomalies leave `statusFilter` empty.
 */
export interface Anomaly {
  /** Stable key used for React lists + test selectors. */
  key: 'rejections' | 'sl_pending';
  /** Human label shown on the chip ("3 rejected" / "5 SL PENDING"). */
  label: string;
  /** The non-routine count this chip represents. */
  count: number;
  /** Status-filter value(s) the chip should set on the host's
   *  ScheduleColumnFilters.status when clicked. Empty array → chip is
   *  informational only (the host renders it as a non-button badge). */
  statusFilter: ScheduleStatusFilter[];
  /** Severity drives the chip color (red = error, amber = warning). */
  severity: 'error' | 'warning';
}

/**
 * Time after which an unplaced SL counts as anomalous on today's view.
 * SLs for overnight positions are re-placed in the morning after entry
 * orders settle; before this window they're legitimately pending and
 * not worth flagging.
 */
export const SL_PLACEMENT_DEADLINE_HMS = '09:20:00';

function compareHms(a: string, b: string): number {
  // String comparison works for "HH:MM:SS" — lexical order matches chronological.
  return a < b ? -1 : a > b ? 1 : 0;
}

export interface ComputeAnomaliesOpts {
  /** Current IST clock as "HH:MM:SS" — needed to gate the SL-pending
   *  anomaly on today's view (don't flag morning-pending SLs before
   *  the deadline). Null when unknown. */
  currentIstHms: string | null;
  /** Is the user viewing today (vs a past/historical date)? On past
   *  dates the time gate doesn't apply — sl_pending > 0 is always an
   *  anomaly because there's no further opportunity to re-place. */
  isToday: boolean;
}

/**
 * Derive the list of non-routine conditions to surface in the
 * AnomalyBanner. Returns an empty array when the day is clean —
 * callers can use that signal to hide the banner entirely.
 *
 * Heuristics:
 *  - REJECTIONS: any entry/SL rejection is always anomalous.
 *  - SL_PENDING: an SL needing morning re-placement is anomalous
 *    only after the placement deadline has passed (today) or on
 *    any past date (where it represents a missed placement).
 *
 * Cancellations are intentionally NOT flagged here — they can be
 * user-initiated (manual exit/cancel) or system-initiated (EOD/
 * expiry) and are routine enough that surfacing them as "needs
 * attention" causes alert fatigue. The Cancelled status box in the
 * StatusBoxRow / hero stats still shows the count for awareness.
 *
 * Pure function; no side effects, no time-of-day reads — caller
 * provides `currentIstHms` so tests stay deterministic.
 */
export function computeAnomalies(
  totals: ScheduleSummaryTotals | null,
  opts: ComputeAnomaliesOpts,
): Anomaly[] {
  if (!totals) return [];

  const out: Anomaly[] = [];

  const rejected = (totals.rejected ?? 0) + (totals.sl_rejected ?? 0);
  if (rejected > 0) {
    out.push({
      key: 'rejections',
      label: `${rejected} rejected`,
      count: rejected,
      statusFilter: ['ERRORS'],
      severity: 'error',
    });
  }

  const slPending = totals.sl_pending ?? 0;
  const slPendingIsAnomaly = slPending > 0 && (
    // Past dates: sl_pending is always anomalous (missed re-placement).
    !opts.isToday
    // Today: only after the morning re-placement deadline.
    || (opts.currentIstHms != null
        && compareHms(opts.currentIstHms, SL_PLACEMENT_DEADLINE_HMS) > 0)
  );
  if (slPendingIsAnomaly) {
    out.push({
      key: 'sl_pending',
      label: `${slPending} SL PENDING`,
      count: slPending,
      // No direct "SL pending" status-filter value — leave empty so
      // the chip renders as informational (no click handler). The
      // existing SLPendingPill inside ScheduleTable already serves
      // the in-table call-to-action; this chip is the day-level alert.
      statusFilter: [],
      severity: 'warning',
    });
  }

  return out;
}
